// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Box, Tooltip, Typography, alpha } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { BarRounded } from '@visx/shape';
import { DateTime } from 'luxon';

const MIN_LABEL_WIDTH = 50;
const MIN_DATE_LABEL_WIDTH = 120;

interface Props {
  y: number;
  height: number;
  scale: AxisScale<number>;
  start: DateTime;
  end: DateTime | null;
  chartEndX: number;
  label: string;
  dateLabel: string;
  tooltipText: string;
  isInactive: boolean;
  color: string;
}

/**
 * A single bar in the line item Gantt chart representing one line item's
 * date range. When end is null (open-ended), renders a solid bar from
 * start followed by a gradient fade to the chart edge.
 */
export function ProjectLineItemGanttBar(props: Props) {
  const startX = props.scale(props.start.toJSDate()) ?? 0;
  const isOpenEnded = props.end === null;

  const solidColor = props.isInactive
    ? alpha('#9e9e9e', 0.4)
    : alpha(props.color, 0.8);

  const gradientId = `open-ended-fade-${props.y}`;

  if (isOpenEnded) {
    // Open-ended: solid bar is just a thin start marker, then fade to chart edge
    const solidWidth = 4;
    const fadeStartX = startX + solidWidth;
    const fadeWidth = Math.max(0, props.chartEndX - fadeStartX);
    const fadeColor = props.isInactive
      ? alpha('#9e9e9e', 0.3)
      : alpha(props.color, 0.4);

    return (
      <g>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={fadeColor} stopOpacity={1} />
            <stop offset="100%" stopColor={fadeColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* Solid start marker */}
        <BarRounded
          left
          radius={4}
          x={startX}
          y={props.y}
          width={solidWidth}
          height={props.height}
          fill={solidColor}
        />
        {/* Fading trail */}
        {fadeWidth > 0 && (
          <rect
            x={fadeStartX}
            y={props.y}
            width={fadeWidth}
            height={props.height}
            fill={`url(#${gradientId})`}
          />
        )}
        {/* Tooltip overlay */}
        <foreignObject
          x={startX}
          y={props.y}
          height={props.height}
          width={Math.max(0, props.chartEndX - startX)}
        >
          <Tooltip title={props.tooltipText} placement="top" arrow>
            <Box
              paddingX={1}
              height="100%"
              display="flex"
              alignItems="center"
              sx={{ cursor: 'default' }}
            >
              <Typography
                variant="caption"
                color="white"
                whiteSpace="nowrap"
                fontWeight={500}
                fontSize="0.65rem"
              >
                {props.label}
              </Typography>
            </Box>
          </Tooltip>
        </foreignObject>
      </g>
    );
  }

  // Closed range: solid bar from start to end. End is guaranteed non-null
  // here because the open-ended branch returns early above.
  const end = props.end!;
  const endX = props.scale(end.toJSDate()) ?? 0;
  const width = Math.max(0, endX - startX);

  return (
    <g>
      <BarRounded
        left
        right
        radius={4}
        x={startX}
        y={props.y}
        width={width}
        height={props.height}
        fill={solidColor}
      />
      {width > 0 && (
        <foreignObject
          x={startX}
          y={props.y}
          height={props.height}
          width={width}
        >
          <Tooltip title={props.tooltipText} placement="top" arrow>
            <Box
              paddingX={width > MIN_LABEL_WIDTH ? 1 : 0}
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              overflow="hidden"
              sx={{ cursor: 'default' }}
            >
              {width > MIN_LABEL_WIDTH && (
                <Typography
                  variant="caption"
                  color="white"
                  whiteSpace="nowrap"
                  fontWeight={500}
                  fontSize="0.65rem"
                >
                  {props.label}
                </Typography>
              )}
              {width > MIN_DATE_LABEL_WIDTH && (
                <Typography
                  variant="caption"
                  color="white"
                  whiteSpace="nowrap"
                  fontSize="0.6rem"
                  sx={{ opacity: 0.8 }}
                >
                  {props.dateLabel}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </foreignObject>
      )}
    </g>
  );
}
